import React, { useContext, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { useDisclosure, Drawer, Divider } from '@chakra-ui/react';

import { BOProfileMenu } from './BOProfileMenu';
import { ProfileButton } from './ProfileButton';
import { ProfileDrawerContents } from './ProfileDrawerContents';
import { IProfile } from '../../../Auth/types/auth';
import { getUserOffersStatus } from '../../../gql';
import { EGTMEvents, useGTMDataLayer } from '../../../hooks';
import { useProfileStore } from '../../../ModalProvider/stores/profile';
import { EColor } from '../../../Theme';
import { NavigationContext } from '../../context/NavigationContext';

interface IProfileDrawerProps {
  profile: IProfile;
  showDivider?: boolean;
  manageAccountUrl: string | undefined;
  enableOfferNotificationBadges?: boolean;
  enableCollections?: boolean;
}

export function ProfileDrawer({
  profile,
  showDivider = false,
  manageAccountUrl,
  enableOfferNotificationBadges,
  enableCollections,
}: IProfileDrawerProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { updateProfile } = useProfileStore();
  const pushGTMData = useGTMDataLayer();
  const { enableNewNav } = useContext(NavigationContext);
  const { data } = useQuery(getUserOffersStatus, {
    skip: !profile || !enableOfferNotificationBadges,
  });

  const hasNewOffersFromSellers =
    data?.collectCurrentUserV2?.hasNewOffersFromSellers ?? false;
  const hasNewOffersFromBidders =
    data?.collectCurrentUserV2?.hasNewOffersFromBidders ?? false;
  const pendingInvoiceCount =
    data?.collectCurrentUserV2?.collectBoInvoiceSummary?.pendingInvoiceCount ??
    profile?.unpaidInvoiceCount ??
    0;
  const overdueInvoicesCount =
    data?.collectCurrentUserV2?.collectBoInvoiceSummary?.overdueInvoiceCount ??
    0;
  const unreadOffersCount =
    data?.collectCurrentUserV2?.collectWaitingOffersCount ?? 0;

  const { usableFunds } = data?.collectCurrentUserV2?.accountFunds ?? {};
  const accountFundsInCents = usableFunds?.amountInCents ?? 0;
  const fancashInCents = data?.fancashBalance?.balance?.amount ?? 0;
  const rewardsInCents = data?.currentBalance?.amount ?? 0;
  const sumInCents = accountFundsInCents + fancashInCents + rewardsInCents;

  const btnRef = React.useRef(null);

  useEffect(() => {
    updateProfile(profile);
  }, [profile, updateProfile]);

  const handleProfileButtonClick = () => {
    pushGTMData({ event: EGTMEvents.PROFILE_BUTTON_CLICKED });
    onOpen();
  };

  return (
    <>
      {showDivider && (
        <Divider
          backgroundColor={EColor.BrandDark}
          color={EColor.White}
          height="30px"
          mx="14px"
          my="0"
          orientation="vertical"
        />
      )}
      <ProfileButton
        enableNewNav={enableNewNav}
        isInverted={false}
        ref={btnRef}
        onClick={handleProfileButtonClick}
      />
      <Drawer
        arial-label="settings"
        finalFocusRef={btnRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        {enableNewNav ? (
          <BOProfileMenu
            enableCollections={enableCollections}
            manageAccountUrl={manageAccountUrl}
            overdueInvoicesCount={overdueInvoicesCount}
            pendingInvoiceCount={pendingInvoiceCount}
            profile={profile}
            sumInCents={sumInCents}
            unreadOffersCount={unreadOffersCount}
            onClose={onClose}
          />
        ) : (
          <ProfileDrawerContents
            isBuyerOffers={hasNewOffersFromSellers}
            isSellerOffers={hasNewOffersFromBidders}
            manageAccountUrl={manageAccountUrl}
            profile={profile}
            onClose={onClose}
          />
        )}
      </Drawer>
    </>
  );
}
